import { NavigateFunction } from 'react-router-dom';
import { FormInstance, message } from 'antd';
import { AxiosError } from 'axios';
import { isEmpty } from 'lodash';

import { initRetailerSuccessAction, IRetailerStore } from 'containers/Retailers/Retailer/store';

import { refetchCommonData } from 'stores';
import { getDateForPayload, getNestedFormChanges } from 'utils';

import { Links } from 'links';

import {
  ApiError,
  CreateRetailerRequest,
  Retailer,
  UpdateRetailerRequest,
} from 'codegen';
import { IApiRequestParams, IDataResponse, RecursiveRequired, TRequestCallback } from 'types';

export const handleRetailerDetailsPostRequest = (
  form: FormInstance<RecursiveRequired<IRetailerStore['initialValues']>>,
  offerOptionsRequest: TRequestCallback<CreateRetailerRequest>,
) => () => {
  const {
    enValues,
    frValues,
    partnerValues,
    retailerValues,
  } = form.getFieldsValue();

  const enLocale = enValues ? [{ locale: 'en-CA', ...enValues }] : [];
  const frLocale = frValues ? [{ locale: 'fr-CA', ...frValues }] : [];

  offerOptionsRequest({
    payload: {
      ...partnerValues,
      ...retailerValues,
      ...(retailerValues?.effectiveTo && { effectiveTo: getDateForPayload(retailerValues.effectiveTo) }),
      ...(retailerValues?.effectiveFrom && { effectiveFrom: getDateForPayload(retailerValues.effectiveFrom) }),
      translations: [...enLocale, ...frLocale],
    },
  });
};

export const handleRetailerDetailsPatchRequest = (
  form: FormInstance<RecursiveRequired<IRetailerStore['initialValues']>>,
  initialValues: IRetailerStore['initialValues'],
  offerOptionsRequest: TRequestCallback<UpdateRetailerRequest>,
) => () => {
  const {
    enValues,
    frValues,
    partnerValues,
    retailerValues,
  } = getNestedFormChanges(initialValues, form);

  const enLocale = !isEmpty(enValues) ? [{ locale: 'en-CA', ...enValues }] : [];
  const frLocale = !isEmpty(frValues) ? [{ locale: 'fr-CA', ...frValues }] : [];

  offerOptionsRequest({
    payload: {
      ...retailerValues,
      ...partnerValues,
      ...(retailerValues?.effectiveTo && { effectiveTo: getDateForPayload(retailerValues.effectiveTo) }),
      ...(retailerValues?.effectiveFrom && { effectiveFrom: getDateForPayload(retailerValues.effectiveFrom) }),
      ...((enLocale.length || frLocale.length) && { translations: [...enLocale, ...frLocale] }),
    },
  });
};

export const handleSuccessPatchRetailerDetails = (
  { data }: IDataResponse<Retailer>,
) => {
  refetchCommonData();
  initRetailerSuccessAction(data);
  message.success(`${data.name} successfully updated!`);
};

export const handleSuccessPostRetailerDetails = (
  navigate: NavigateFunction,
) => (
  { data }: IDataResponse<Retailer>,
) => {
  refetchCommonData();
  navigate(`${Links.Retailers}/${data.id}`, { replace: true });
  message.success(`${data.name} successfully created!`);
};

export const handleRetailerRequestError = (
  form: FormInstance<RecursiveRequired<IRetailerStore['initialValues']>>,
) => (
  { response }: AxiosError<ApiError, Retailer>,
  [{ payload }]: [IApiRequestParams<UpdateRetailerRequest>],
) => {
  const formEn = payload?.translations?.find((tr) => tr.locale === 'en-CA') && form;
  const formFr = payload?.translations?.find((tr) => tr.locale === 'fr-CA') && form;

  if (formEn) {
    formEn.setFields([{
      name: ['enValues', 'nameTemplate'],
      errors: [response?.data.message as string],
    }]);
  }

  if (formFr) {
    formFr.setFields([{
      name: ['frValues', 'nameTemplate'],
      errors: [response?.data.message as string],
    }]);
  }
};
